import { MapPin, Mail } from "lucide-react";
import { Badge } from "./ui/badge";
import moment from "moment";

interface Homeowner {
  id: string;
  userId: string;
  name: string;
  email: string;
  phone: string;
  city: string;
  state: string;
  zipCode: string;
  profileImage: string;
  totalLeads: number;
  activeLeads: number;
  completedProjects: number;
  contractsCreated: number;
  contractorContacted: number;
  contractsSigned: number;
  status: "active" | "blocked" | "pending";
  joinedDate: string;
  lastActive: string;
}

interface HomeownerListRowProps {
  homeowner: Homeowner;
  onClick: () => void;
}


export function HomeownerListRow({ homeowner, onClick }: HomeownerListRowProps) {

  const getStatusColor = (status: string) => {
    switch (status) {
      case "active":
        return "bg-green-50 text-green-700 border-green-200";
      case "blocked":
        return "bg-red-50 text-red-700 border-red-200";
      case "pending":
        return "bg-yellow-50 text-yellow-700 border-yellow-200";
      default:
        return "bg-gray-50 text-gray-700 border-gray-200";
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case "active":
        return "Active";
      case "blocked":
        return "Blocked";
      case "pending":
        return "Pending";
      default:
        return status;
    }
  };

  return (
    <button
      type="button"
      onClick={onClick}
      className="homeowner-list-row bg-white rounded-lg border border-gray-200 hover:shadow-md hover:border-blue-300 transition-all duration-200 cursor-pointer"
      aria-label={`View ${homeowner.name || "homeowner"} details`}
    >
        <div className="homeowner-list-row__identity">
          {homeowner.profileImage ? (
            <img
              src={homeowner.profileImage}
              alt={homeowner.name}
              className="homeowner-list-row__avatar rounded-full object-cover border-2 border-gray-100 flex-shrink-0"
            />
          ) : (
            <div className="homeowner-list-row__avatar rounded-full bg-gray-200 flex items-center justify-center text-gray-700 font-semibold border-2 border-gray-100 flex-shrink-0">
              {homeowner.name
                ?.split(" ")
                .map((n) => n[0])
                .join("")
                .slice(0, 2)
                .toUpperCase()}
            </div>
          )}

          <div className="min-w-0">
            <div className="font-semibold text-gray-900 truncate">
              {homeowner.name}
            </div>

            <div className="text-sm text-gray-500">
              ID: {homeowner.userId}
            </div>

            <div className="text-xs text-gray-400">
              Joined: {homeowner.joinedDate
                ? moment(homeowner.joinedDate).format("MMM D, YYYY")
                : "N/A"}
            </div>
          </div>
        </div>

        <div className="homeowner-list-row__email">
          <Mail className="w-4 h-4 text-gray-400 flex-shrink-0" />
          <span className="text-sm text-gray-600 truncate">
            {homeowner.email}
          </span>
        </div>

        <div className="homeowner-list-row__location">
          <MapPin className="w-4 h-4 text-gray-400 flex-shrink-0" />
          <span className="text-sm text-gray-600">
            {[homeowner.city, homeowner.state].filter(Boolean).join(", ") || "Location not available"}
          </span>
        </div>

        <div className="homeowner-list-row__leads bg-blue-50 rounded-md px-3 py-1.5 border border-blue-100">
          <div className="text-xs text-blue-600">Active Leads</div>
          <div className="font-semibold text-blue-700">
            {homeowner.activeLeads}
          </div>
        </div>

        <div className="homeowner-list-row__status">
          <Badge variant="outline" className={`${getStatusColor(homeowner.status)}`}>
            {getStatusLabel(homeowner.status)}
          </Badge>
        </div>

    </button>
  );
}
